import { DeepPartial, ISeriesApi, LogicalRange, PriceScaleMode } from 'lightweight-charts'
import {
    PaneConfig,
    Series,
    SeriesData,
    SeriesGroup as SeriesGroupType,
    SeriesOptions,
    SeriesSourceOptions
} from './chart.typings'
import Chart from './chart'
import {
    DataUpdateRequestType,
    getIndicatorDrawingSettings,
    getIndicatorOutputPriceFormat,
    getPercentageModeBaseLineSeriesOptions,
    getPriceScaleId,
    getViewportCandleInfo,
    isTransformedChartType,
    COMPARABLE_SECURITY_SOURCE_INPUT_CODE,
} from '../../util/chart'
import merge from 'lodash/merge'
import { SeriesGroupCategory } from '../../pages/home'
import { BLANK_SERIES_ID, VOLUME_SCALE } from '../../assets/constants'
import { ChartSubType, ChartType } from '../../proto/generated/chart'
import { IndicatorOutput_OutputType } from '../../proto/generated/indicator'
import DrawingBase from './drawingTools/drawing.base'
import { DrawingDisplayType, DrawingSettingsMap } from './drawingTools/drawing.interface'
import BaseChartSeries from './chartSeries/baseSeries'
import CandleStickSeries from './chartSeries/candleStickSeries'
import LineSeries from './chartSeries/lineSeries'
import BarSeries from './chartSeries/barSeries'
import AreaSeries from './chartSeries/areaSeries'
import FillSeries from './chartSeries/fillSeries'
import HistogramSeries from './chartSeries/histogramSeries'
import HeikinAshiSeries from './chartSeries/heikinAshiSeries'
import BaselineSeries from './chartSeries/baselineSeries'
import HollowCandleSeries from './chartSeries/hollowCandleSeries'
import HighLowCandleSeries from './chartSeries/highLowCandleSeries'
import HlcAreaSeries from './chartSeries/hlcAreaSeries'
import RenkoSeries from './chartSeries/renkoSeries'
import KagiSeries from './chartSeries/kagiSeries'
import PandFSeries from './chartSeries/pandFSeries'
import LineBreakSeries from './chartSeries/lineBreakSeries'
import VolumeCandleSeries from './chartSeries/volumeCandleSeries'
import StepLineSeries from "./chartSeries/stepLineSeries";
import LineWithMarkersSeries from './chartSeries/lineWithMarkersSeries'
import { getBaseSecurityKeyFromComparableToken } from '../../util/security'

export type SeriesGroupOptions = {
    config: SeriesGroupType,
    series: Series[]
    pane: PaneConfig,
    paneIndex: number,
    primary: boolean,
    chart: Chart

}

export default class SeriesGroup {
    config: SeriesGroupType
    series: Series[]
    pane: PaneConfig
    paneIndex: number
    primary: boolean
    chart: Chart
    seriesDrawings: Record<string, DrawingBase<any, any, any>> = {}
    comparableModeIndexStart = -1
    chartSeries: Record<string, BaseChartSeries> = {}
    constructor(options: SeriesGroupOptions) {
        this.config = options.config
        this.series = options.series
        this.pane = options.pane
        this.paneIndex = options.paneIndex
        this.primary = options.primary
        this.chart = options.chart
    }

    async initialize() {
        this.createAllSeries()
    }

    reset() { }

    updateConfig(options: SeriesGroupOptions) {
        this.config = options.config
        this.series = options.series
        this.pane = options.pane
        this.paneIndex = options.paneIndex
        this.primary = options.primary
        this.chart = options.chart
        this.onConfigUpdate()
    }

    onConfigUpdate() { }
    getSeriesSource(): SeriesSourceOptions['source'] {
        return "close"
    }
    getChartSeries(type: ChartType, seriesConfig: Series, paneIndex: number, options: any, chartSubType?: ChartSubType): BaseChartSeries {
        switch (type) {
            case ChartType.CANDLESTICK:
                return new CandleStickSeries(this.chart, seriesConfig, this.config, paneIndex, options)
            case ChartType.LINE:
                return new LineSeries(this.chart, seriesConfig, this.config, paneIndex, options)
            case ChartType.BAR:
                return new BarSeries(this.chart, seriesConfig, this.config, paneIndex, options)
            case ChartType.AREA:
                return new AreaSeries(this.chart, seriesConfig, this.config, paneIndex, options)
            case ChartType.FILL:
                return new FillSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.HISTOGRAM:
            case ChartType.COLUMN:
                return new HistogramSeries(this.chart, seriesConfig, this.config, paneIndex, options)
            case ChartType.HEIKIN_ASHI:
                return new HeikinAshiSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.HOLLOW_CANDLES:
                return new HollowCandleSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.HIGH_LOW:
                return new HighLowCandleSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.BASELINE:
                return new BaselineSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.HLC_AREA:
                return new HlcAreaSeries(this.chart, seriesConfig, this.config, paneIndex, options)
            case ChartType.RENKO:
                return new RenkoSeries(this.chart, seriesConfig, this.config, paneIndex, options)
            case ChartType.KAGI:
                return new KagiSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.POINT_AND_FIGURE:
                return new PandFSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.LINE_BREAK:
                return new LineBreakSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.VOLUME_CANDLESTICK:
                return new VolumeCandleSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.STEP_LINE_CHART:
                return new StepLineSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            case ChartType.LINE_WITH_MARKERS:
                return new LineWithMarkersSeries(this.chart, seriesConfig, this.config, paneIndex, options);
            default:
                return new CandleStickSeries(this.chart, seriesConfig, this.config, paneIndex, options)
        }
    }

    createAllSeries(primaryOnly = false) {
        const blankSeriesId = this.series[1]?.id ?? BLANK_SERIES_ID;
        this.series.forEach((series) => {
            if (primaryOnly && series.id === blankSeriesId) return;
            this.createSeries(series);
        })

        if (this.config.primary) {
            this.chartSeries[blankSeriesId]?.seriesApi.applyOptions({ visible: false })
        }
    }
    getSeriesOptions(chartType: ChartType, config: Series): DeepPartial<SeriesOptions> {
        const { id, priceFormat, visibility = true, options } = config
        const { category, outputValues, scale, compareMode } = this.config
        const isPrimary = category === SeriesGroupCategory.SERIES_SECURITY
        const settings = this.chart.chartConfig?.scalesAndLinesSettings
        const scaleMode = this.getPriceScaleModeForGroupScale()

        return {
            priceScaleId:
                category === SeriesGroupCategory.SERIES_VOLUME
                    ? VOLUME_SCALE
                    : this.chart.overlayPriceScale ? "OVERLAY_PRICE_SCALE" : getPriceScaleId(scale, this.chart.defaultPriceScaleId),
            pane: this.paneIndex,
            ...options,
            priceFormat: getIndicatorOutputPriceFormat(priceFormat, this.config.outputValuesPrecision),
            ...getPercentageModeBaseLineSeriesOptions(scaleMode, this.chart.theme),
            ...(category === SeriesGroupCategory.SERIES_COMPARABLE_SECURITY && this.config.inputValues?.[COMPARABLE_SECURITY_SOURCE_INPUT_CODE]
                ? { source: this.config.inputValues[COMPARABLE_SECURITY_SOURCE_INPUT_CODE] }
                : {}),
            visible: visibility,
            // Symbol last-price line/label are controlled by the global scales-and-lines
            // settings, so they must override any persisted per-series `options`.
            priceLineVisible: isPrimary ? (settings?.showSymbolLine ?? true) : false,
            ...(isPrimary ? { lastValueVisible: settings?.showSymbolValue ?? true } : {})
        }
    }

    private getPriceScaleModeForGroupScale(): PriceScaleMode {
        if (this.config.category === SeriesGroupCategory.SERIES_VOLUME) {
            return PriceScaleMode.Normal
        }
        const scaleOptions = this.chart.chartConfig?.scaleOptions
        if (!scaleOptions) {
            return PriceScaleMode.Normal
        }
        return this.config.scale === 'LEFT' ? scaleOptions.leftScaleMode : scaleOptions.rightScaleMode
    }
    createSeries(config: Series) {
        const { id, chartType, drawing, chartSubType } = config
        const {
            drawingType = undefined,
            drawingSettings = undefined
        } = this?.config?.outputValues?.[config.id] ?? {}
        if (!this.chart.chartApi) return
        if (!drawing) {
            this.chartSeries[id] = this.getChartSeries(chartType, config, this.paneIndex, this.getSeriesOptions(chartType, config), chartSubType)
        } else if (drawingType) {
            // Head & shoulders indicator pattern is created by the script draw callback with detected points.
            if (drawingType === 'chartPatternOverlay') {
                return
            }
            const pSg = this.chart?.primarySeriesGroup
            const dr = this.chart.drawingManager.addDrawing(drawingType as keyof DrawingSettingsMap, DrawingDisplayType.INDICATOR, {
                drawingType: drawingType,
                persist: false
            }, {
                ...getIndicatorDrawingSettings(drawingType as keyof DrawingSettingsMap, this.chart.chartApi.options(), drawingSettings ?? {}),
                seriesGroupId: this.config.id,
            }, undefined, undefined, pSg?.pane?.id, pSg?.series?.[0]?.id)
            if (dr !== null) {
                dr.visible = config.visibility ?? true
                this.seriesDrawings[config.id] = dr!
                dr.updateSettings({ seriesGroupId: this.config.id })
            }
        }
    }

    recreateSeries(id: string) {
        const series = this.series.find(s => s.id === id);
        if (!series) return;
        const chartSeries = this.chartSeries[id];

        if (chartSeries) {
            const data = chartSeries.getRawData()
            chartSeries.destroy();
            delete this.chartSeries[id];
            this.createSeries(series)
            this.chartSeries[id]?._setData(data)
        }
    }

    getDrawingIds(): string[] {
        if (!this.chart) {
            return []
        }
        return Object.values(this.seriesDrawings)?.map((sd) => sd?.id)
    }

    getSeriesIdOfDrawingId(drawingId: string): string | undefined {
        if (!this.chart) {
            return undefined
        }
        let seriesId: string | undefined = undefined
        Object.entries(this.seriesDrawings)?.forEach((entry) => {
            if (entry[1]?.id === drawingId) {
                seriesId = entry[0]
            }
        })
        return seriesId
    }

    onVisibleLogicalRangeChange = (range: LogicalRange | null) => {
        const chartSeries = this.chartSeries[this.series[0].id]
        if (!range) return
        const barsInfo = chartSeries.getBarsInfo(range)
        if (!barsInfo) return

        const viewportCandleInfo = getViewportCandleInfo(range, barsInfo, chartSeries.getData())
        this.chart.onVisibleLogicalRangeChange(viewportCandleInfo)
    }

    updateData = (newData: boolean, updateType?: DataUpdateRequestType) => {
        const { securityList, category, primary } = this.config
        let key = securityList[0]
        let data = this.chart.getRawData()
        const { id } = this.series[0]
        switch (category) {
            case SeriesGroupCategory.SERIES_SECURITY: {
                if (!newData) return
                if (!data.length) {
                    this.chartSeries[id]._setData([])
                    const blankSeriesId = this.series[1]?.id ?? BLANK_SERIES_ID
                    this.chartSeries[blankSeriesId]?._setData([])
                    return
                }
                if (primary) {
                    const blankSeriesId = this.series[1]?.id ?? BLANK_SERIES_ID;
                    const useFutureWhitespace = !this.chart.noWhitespace
                        && !isTransformedChartType(this.chart.chartConfig.chartType)
                    if (updateType === DataUpdateRequestType.LIVE) {
                        const lastCandle = data[data.length - 1];
                        this.chartSeries[id]._update(lastCandle);

                        if (useFutureWhitespace) {
                            let emptyCandle = this.chart.emptyTimeSlots[0];
                            let c = 0;
                            while (emptyCandle && emptyCandle.time < lastCandle.time) {
                                c += 1;
                                emptyCandle = this.chart.emptyTimeSlots[c];
                            }

                            if (c > 0) {
                                this.chart.emptyTimeSlots = this.chart.emptyTimeSlots.slice(c);
                                this.chartSeries[blankSeriesId]?._setData(this.chart.emptyTimeSlots);
                            }
                        } else if (this.chart.noWhitespace) {
                            this.chartSeries[blankSeriesId]?._update(lastCandle);
                        }
                    } else {
                        if (useFutureWhitespace) {
                            this.chartSeries[blankSeriesId]?._setData(this.chart.emptyTimeSlots);
                        } else if (this.chart.noWhitespace) {
                            this.chartSeries[blankSeriesId]?._setData(data);
                        } else {
                            this.chartSeries[blankSeriesId]?._setData([]);
                        }
                        this.chartSeries[id]._setData(data);
                    }

                }
                break
            }

            case SeriesGroupCategory.SERIES_COMPARABLE_SECURITY: {
                const dataKey = getBaseSecurityKeyFromComparableToken(key)
                data = this.chart.getData(dataKey) || []
                const primaryData = this.chart.getRawData()

                if (!data.length || !primaryData.length) {
                    this.chartSeries[id]._setData([])
                    return
                }
                if (updateType === DataUpdateRequestType.LIVE) {
                    const lastCandle = data[data.length - 1]
                    this.chartSeries[id]._update(lastCandle)
                } else if (updateType === DataUpdateRequestType.INITIAL) {
                    this.chartSeries[id]._setData(data)
                } else {
                    this.chartSeries[id]._setData(data)
                }
            }

        }
    }

    getDrawing(): DrawingBase<any, any, any> | null {
        if (this.config.category !== SeriesGroupCategory.SERIES_DRAWING) {
            return null
        }
        return Object.values(this.seriesDrawings)?.[0] ?? null
    }

    getPrimarySeries(): BaseChartSeries | undefined {
        if (!this.config.primary) return
        const { id } = this.series[0]
        return this.chartSeries[id]
    }

    getPrimarySeriesApi(): ISeriesApi<any> | undefined {
        if (!this.config.primary) return

        const { id } = this.series[0]
        return this.chartSeries[id]?.seriesApi
    }

    removeSeries(id: string) {
        const series = this.chartSeries[id]
        if (!series) return
        series.destroy()
        delete this.chartSeries[id]
    }

    removeAllSeries(primaryOnly = false) {
        const blankSeriesId = this.series[1]?.id ?? BLANK_SERIES_ID;

        this.series.forEach(({ id, drawing }) => {
            if (primaryOnly && id === blankSeriesId) return;

            if (drawing) {
                this.removeDrawing(id)
            } else {
                this.removeSeries(id)
            }
        })
    }

    removeDrawing(id: string) {
        const dr = this.seriesDrawings[id]
        if (dr) {
            dr.destroy()
        }
        delete this.seriesDrawings[id]
    }

    resolveDrawingVisibility(seriesId: string): boolean {
        const output = this.config.outputValues?.[seriesId]
        const series = this.series.find((s) => s.id === seriesId)
        return (output?.visibility ?? series?.visibility ?? true) !== false
    }

    /** Resolve indicator pattern drawing — also links orphan drawings from draw callback */
    resolveSeriesDrawing(seriesId: string): DrawingBase<any, any, any> | undefined {
        const cached = this.seriesDrawings[seriesId]
        if (cached) return cached

        const series = this.series.find((s) => s.id === seriesId)
        const patternTitle = series?.title
        if (!patternTitle) return undefined

        for (const drawing of Object.values(this.chart.drawingManager.drawings)) {
            if (
                drawing.displayType === DrawingDisplayType.INDICATOR
                && drawing._settings?.indicator
                && drawing._settings?.patternTitle === patternTitle
            ) {
                this.seriesDrawings[seriesId] = drawing
                return drawing
            }
        }
        return undefined
    }

    private isDrawingOnlyOutput(seriesId: string): boolean {
        const output = this.config.outputValues?.[seriesId]
        return Boolean(
            output?.drawingType
            || output?.type === IndicatorOutput_OutputType.DRAWING
            || this.seriesDrawings[seriesId],
        )
    }

    private hasOnlyDrawingOutputs(): boolean {
        return this.series.every(
            (s) => s.drawing || this.isDrawingOnlyOutput(s.id),
        )
    }

    /** Runtime visibility for metadata eye icon — prefers live drawing/series state */
    getGroupVisibility(): boolean {
        for (const seriesId of this.config.series ?? []) {
            const drawing = this.resolveSeriesDrawing(seriesId)
            if (drawing) {
                return drawing.visible !== false
            }
            const chartSeries = this.chartSeries[seriesId]
            if (chartSeries) {
                const options = chartSeries.seriesApi.options()
                return options.visible !== false
            }
        }
        const firstSeriesId = this.config.series?.[0]
        return firstSeriesId ? this.resolveDrawingVisibility(firstSeriesId) : true
    }

    updateOutputDrawingSettings(seriesId: string) {
        const output = this.config.outputValues?.[seriesId]
        const drawing = this.resolveSeriesDrawing(seriesId)
        if (!drawing) return

        drawing.visible = this.resolveDrawingVisibility(seriesId)

        if (!output?.drawingType) {
            drawing.primitive?.requestUpdate()
            return
        }

        const baseSettings = getIndicatorDrawingSettings(
            output.drawingType as keyof DrawingSettingsMap,
            this.chart.chartApi.options(),
            output.drawingSettings ?? {}
        )
        const labelColorBullish = output.drawingSettings?.settings?.labelColorBullish
            ?? output.colors?.find((c) => c.title === 'Label Color Bullish')?.colorCode
            ?? output.colors?.find((c) => c.title === 'Color 0')?.colorCode

        const labelColorBearish = output.drawingSettings?.settings?.labelColorBearish
            ?? output.colors?.find((c) => c.title === 'Label Color Bearish')?.colorCode

        drawing.updateSettings({
            ...drawing._settings,
            ...baseSettings,
            ...(labelColorBullish ? { labelColorBullish } : {}),
            ...(labelColorBearish ? { labelColorBearish } : {}),
        })
        drawing.primitive?.requestUpdate()
    }

    getSeriesData(offset: number): Record<string, SeriesData[]> {
        const data: Record<string, SeriesData[]> = {}
        const { securityList, category, primary } = this.config
        let key = securityList[0]
        this.series.forEach(({ id }) => {
            let seriesData: SeriesData[] = []
            seriesData = this.chartSeries[id]?.getData();

            if (!seriesData) {
                data[id] = []
                return
            }

            const index = seriesData.length - (offset || 1) - 2
            if (index + 1 < 0) {
                data[id] = seriesData ? [seriesData[0]] : []
            } else {
                data[id] = seriesData ? seriesData.slice(index + 1, index + 3) : []
            }
        })

        return data
    }

    persistSeriesVisibility(seriesId: string, visible: boolean) {
        const groupId = this.config.id
        const nextOutputValues = {
            ...(this.config.outputValues ?? {}),
        }
        const output = nextOutputValues[seriesId]
        nextOutputValues[seriesId] = output
            ? { ...output, visibility: visible }
            : { visibility: visible }

        this.config = {
            ...this.config,
            outputValues: nextOutputValues,
        }
        this.series = this.series.map((s) =>
            s.id === seriesId ? { ...s, visibility: visible } : s,
        )

        const { chartConfig } = this.chart
        const nextSeries = chartConfig.series[seriesId]
            ? {
                ...chartConfig.series,
                [seriesId]: { ...chartConfig.series[seriesId], visibility: visible },
            }
            : chartConfig.series

        this.chart.chartConfig = {
            ...chartConfig,
            series: nextSeries,
            seriesGroups: groupId && chartConfig.seriesGroups[groupId]
                ? {
                    ...chartConfig.seriesGroups,
                    [groupId]: {
                        ...chartConfig.seriesGroups[groupId],
                        outputValues: nextOutputValues,
                    },
                }
                : chartConfig.seriesGroups,
        }
    }

    private publishVisibilityConfigUpdate() {
        this.chart.callbacks.onConfigUpdate({ ...this.chart.chartConfig })
    }

    toggleVisibility(visible: boolean) {
        const toggledSeriesIds = new Set<string>()

        for (const seriesId of this.config.series ?? []) {
            const drawing = this.resolveSeriesDrawing(seriesId)
            if (drawing) {
                drawing.visible = visible
                this.persistSeriesVisibility(seriesId, visible)
                this.updateOutputDrawingSettings(seriesId)
                drawing.primitive?.requestUpdate()
                toggledSeriesIds.add(seriesId)
            }
        }

        if (toggledSeriesIds.size > 0) {
            this.chart.chartApi?.panes()?.forEach((pane) => pane.requestUpdate?.())
            this.publishVisibilityConfigUpdate()
            return
        }

        const drawingEntries = Object.entries(this.seriesDrawings)
        if (drawingEntries.length > 0) {
            drawingEntries.forEach(([seriesId, drawing]) => {
                drawing.visible = visible
                this.persistSeriesVisibility(seriesId, visible)
                this.updateOutputDrawingSettings(seriesId)
                drawing.primitive?.requestUpdate()
            })
            this.chart.chartApi?.panes()?.forEach((pane) => pane.requestUpdate?.())
            if (this.config.category === SeriesGroupCategory.SERIES_DRAWING || this.hasOnlyDrawingOutputs()) {
                this.publishVisibilityConfigUpdate()
                return
            }
        } else if (this.config.category === SeriesGroupCategory.SERIES_DRAWING) {
            return
        }

        const sG = this.chart.primarySeriesGroup
        if (!sG) return
        const blankId = sG.series[1]?.id ?? BLANK_SERIES_ID
        Object.entries(this.chartSeries).forEach(([seriesId, series]) => {
            if (series.config.id === blankId) return
            series.seriesApi.applyOptions({ visible })
            this.persistSeriesVisibility(seriesId, visible)
        })
        this.publishVisibilityConfigUpdate()
    }

    refreshGroup(recreate = true, primaryOnly = false) {
        if (recreate) {
            if (this.primary) {
                this.unsubscribeVisibleLogicalRangeChange()
                this.chart.removeHighLowLines()
                this.chart.debouncedFetchDataOnViewportChange.cancel()
            }
            this.removeAllSeries(primaryOnly)
            this.createAllSeries(primaryOnly)
            this.updateData(true, DataUpdateRequestType.INITIAL)
            // Reattach overlay price scale if this is the primary series group
            if (this.primary) {
                this.chart.attachOverlayPriceScale()
                this.chart.updateHighLowLines()
                this.chart.updateDayHighLowLines()
                this.updateScreenerBaseline()
                this.subscribeVisibleLogicalRangeChange()
            }
        } else {
            const sG = this.chart.primarySeriesGroup
            if (!sG) return
            const blankId = sG.series[1]?.id ?? BLANK_SERIES_ID
            this.series.forEach((series) => {
                if (series.id === blankId) {
                    return;
                }
                // Drawing outputs (e.g. OI Profile) use seriesDrawings, not lightweight-charts series.
                if (series.drawing || this.seriesDrawings[series.id]) {
                    return;
                }
                const chartSeries = this.chartSeries[series.id]
                if (!chartSeries) {
                    return;
                }
                const seriesOptions = this.getSeriesOptions(series.chartType, series);
                chartSeries.options = merge({}, chartSeries.options, seriesOptions);
                chartSeries.updateConfig(series);
                chartSeries.seriesApi.applyOptions(seriesOptions);
            })
        }
    }

    refreshCompareMode() {
        this.config.compareMode = this.chart.compareMode
    }

    updateScreenerBaseline() {
        if (!this.primary || !this.chart.screenerChart || this.chart.chartConfig.chartType !== ChartType.BASELINE) return
        const primarySeriesConfig = this.series[0]
        if (!primarySeriesConfig) return
        const baselineSeries = this.chartSeries[primarySeriesConfig.id]
        if (baselineSeries instanceof BaselineSeries) {
            baselineSeries.updateScreenerBaseValue()
        }
    }

    subscribeVisibleLogicalRangeChange() {
        if (this.chart.destroyed || !this.chart.chartApi) return
        this.chart.chartApi.timeScale().subscribeVisibleLogicalRangeChange(this.onVisibleLogicalRangeChange)
    }

    unsubscribeVisibleLogicalRangeChange() {
        if (this.chart.destroyed || !this.chart.chartApi) return
        this.chart.chartApi.timeScale().unsubscribeVisibleLogicalRangeChange(this.onVisibleLogicalRangeChange)
    }

    destroy(pushToHistory = false) {
        this.removeAllSeries()
        this.unsubscribeVisibleLogicalRangeChange()
    }
}